import { SERVER_URL } from "../../../constants";
import {
  fetchResidentsShared,
  createUpdateRoomResidents,
  generateFireListCSV,
  generateRoomListPDF,
} from "../utils/roomAssignmentUtils";

const inFlightResidentRequests = new Map();

function buildResidentsKey({ date, campus }) {
  return `${date || ""}|${campus || ""}`;
}

export async function fetchBedroomResidents({ date, campus }) {
  const requestKey = buildResidentsKey({ date, campus });

  if (inFlightResidentRequests.has(requestKey)) {
    return inFlightResidentRequests.get(requestKey);
  }

  const token = sessionStorage.getItem("bearer");

  const requestPromise = (async () => {
    try {
      return await fetchResidentsShared({ date, token, campus });
    } finally {
      inFlightResidentRequests.delete(requestKey);
    }
  })();

  inFlightResidentRequests.set(requestKey, requestPromise);
  return requestPromise;
}

export async function fetchBedrooms({ date, campus, residents }) {
  const token = sessionStorage.getItem("bearer");

  const response = await fetch(
    `${SERVER_URL}api/bedrooms/board?campus=${encodeURIComponent(campus)}&date=${date}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch bedroom board data");
  }

  const bedrooms = await response.json();

  return bedrooms.map((bedroom) => {
    const studentIds = (bedroom.residents || [])
      .filter((r) => r.residentType === "student")
      .map((r) => r.residentId);

    const adultIds = (bedroom.residents || [])
      .filter((r) => r.residentType === "adult")
      .map((r) => r.residentId);

    const matchedStudents = residents.filter(
      (r) => r.type === "student" && studentIds.includes(r.rawId)
    );

    const matchedAdults = residents.filter(
      (r) => r.type === "adult" && adultIds.includes(r.rawId)
    );

    return {
      id: bedroom.bedroomId,
      groupName: bedroom.bedroomName,
      bedroomName: bedroom.bedroomName,
      roomName: bedroom.bedroomName,
      campus: bedroom.campus,
      capacity: bedroom.capacity,
      notes: bedroom.notes,
      house: bedroom.house,
      ensuite:
        bedroom.ensuite === true ||
        bedroom.ensuite === 1 ||
        bedroom.ensuite === "1" ||
        bedroom.ensuite === "true",
      residents: [...matchedStudents, ...matchedAdults],
      studentIds,
      adultIds,
    };
  });
}

export async function fetchBedroomsForYear({ year }) {
  const token = sessionStorage.getItem("bearer");

  const response = await fetch(
    `${SERVER_URL}api/bedrooms/management?year=${encodeURIComponent(year)}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch rooms for management");
  }

  const rooms = await response.json();

  return (rooms || []).map((room) => ({
    id: room.roomId,
    roomId: room.roomId,
    groupName: room.roomName,
    bedroomName: room.roomName,
    roomName: room.roomName,
    campus: room.campus,
    capacity: room.capacity,
    notes: room.notes,
    house: room.house,
    ensuite:
      room.ensuite === true ||
      room.ensuite === 1 ||
      room.ensuite === "1" ||
      room.ensuite === "true",
    allocatedResidentCount: room.allocatedResidentCount ?? 0,
    hasAllocatedResidents: Boolean(room.hasAllocatedResidents),
    scheduledDates: Array.isArray(room.scheduledDates) ? room.scheduledDates : [],
    scheduledDayCount:
      room.scheduledDayCount ??
      (Array.isArray(room.scheduledDates) ? room.scheduledDates.length : 0),
  }));
}

export function buildSaveBedroomAssignments(args) {
  return createUpdateRoomResidents(args);
}

function buildBedroomPayload(bedroomData) {
  return {
    bedroomName:
      bedroomData.bedroomName ||
      bedroomData.roomName ||
      bedroomData.groupName ||
      "",
    campus: bedroomData.campus,
    capacity:
      bedroomData.capacity === "" || bedroomData.capacity == null
        ? null
        : parseInt(bedroomData.capacity, 10),
    notes: bedroomData.notes || "",
    house: bedroomData.house || "",
    ensuite: Boolean(bedroomData.ensuite),
  };
}

export async function createBedroom(bedroomData) {
  const token = sessionStorage.getItem("bearer");

  const response = await fetch(`${SERVER_URL}api/bedrooms`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(buildBedroomPayload(bedroomData)),
  });

  if (!response.ok) {
    throw new Error("Failed to create bedroom");
  }

  return response.json();
}

export async function updateBedroom(bedroomData) {
  const token = sessionStorage.getItem("bearer");

  if (!bedroomData?.id) {
    throw new Error("Bedroom id is required for update");
  }

  const response = await fetch(`${SERVER_URL}api/bedrooms/${bedroomData.id}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(buildBedroomPayload(bedroomData)),
  });

  if (!response.ok) {
    throw new Error("Failed to update bedroom");
  }

  return response.json();
}

export async function deleteBedroom(roomId) {
  const token = sessionStorage.getItem("bearer");

  if (!roomId) {
    throw new Error("Bedroom id is required for delete");
  }

  const response = await fetch(`${SERVER_URL}api/bedrooms/${roomId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || "Failed to delete bedroom");
  }

  return true;
}

export async function bulkUpdateBedrooms(rooms) {
  const token = sessionStorage.getItem("bearer");

  const response = await fetch(`${SERVER_URL}api/bedrooms/bulk-update`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ rooms }),
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || "Failed to bulk update bedrooms");
  }

  return response.json();
}

export async function exportBedroomFireList({ displayState, formattedDate, showToast }) {
  return generateFireListCSV(displayState, formattedDate, showToast);
}

export async function exportBedroomPdf({ displayState, formattedDate, selectedCampus, showToast }) {
  return generateRoomListPDF(displayState, formattedDate, selectedCampus, showToast);
}

export async function fetchAvailableScheduleDates(year, campus) {
  const token = sessionStorage.getItem("bearer");

  const query = new URLSearchParams({
    year: String(year),
    ...(campus ? { campus } : {}),
  });

  const response = await fetch(
    `${SERVER_URL}api/bedrooms/schedule-dates?${query.toString()}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch available schedule dates");
  }

  return response.json();
}

export async function saveRoomSchedule(roomId, year, dates) {
  const token = sessionStorage.getItem("bearer");

  const response = await fetch(
    `${SERVER_URL}api/bedrooms/${roomId}/schedule?year=${year}`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ dates }),
    }
  );

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || "Failed to save room schedule");
  }

  return response.json();
}